import { useState } from "react";
import { motion } from "framer-motion";
import type { PrivacyMode } from "../types";
import { PRIVACY_DEFAULTS } from "../types";
import { RotatingWords } from "./TypewriterText";
import { CountdownTimer } from "./fomo/CountdownTimer";
import { SignupProgressBar } from "./fomo/SignupProgressBar";
import { ScarcityBadge } from "./fomo/ScarcityBadge";
import { LiveActivityFeed } from "./fomo/LiveActivityFeed";

// Genesis launch date: 30 days from now
const GENESIS_LAUNCH_DATE = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
const CURRENT_SIGNUPS = 847;
const TOTAL_SPOTS = 1000;

// =============================================================================
// Types
// =============================================================================

interface SessionLobbyProps {
  onStartSession: (mode: PrivacyMode) => void;
  hasActiveSession: boolean;
  onLearnMore?: () => void;
}

interface PrivacyOption {
  mode: PrivacyMode;
  title: string;
  description: string;
  details: string[];
  icon: string;
}

// =============================================================================
// Constants
// =============================================================================

const PRIVACY_OPTIONS: PrivacyOption[] = [
  {
    mode: "standard",
    title: "Standard",
    description: "Balanced performance with sensible data retention.",
    details: [
      "1 hour scratch memory TTL",
      "Pinning allowed",
      "Remote storage enabled",
      "Full agent mesh access",
    ],
    icon: "\u25CB", // circle
  },
  {
    mode: "high-sensitivity",
    title: "High Sensitivity",
    description: "Maximum privacy. All data ephemeral, no remote storage.",
    details: [
      "5 minute scratch memory TTL",
      "Pinning disabled",
      "Device-only storage",
      "Encrypted agent channels",
    ],
    icon: "\u25C9", // fisheye
  },
  {
    mode: "offline",
    title: "Offline",
    description: "Air-gapped mode. No network, local execution only.",
    details: [
      "30 minute scratch memory TTL",
      "Pinning allowed (local)",
      "No remote storage",
      "Local agents only",
    ],
    icon: "\u25CE", // bullseye
  },
];

// =============================================================================
// Particle Background
// =============================================================================

function ParticleBackground() {
  // Generate a fixed set of particles for the background drift effect
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: `${(i * 2) % 100}%`,
    size: 1 + (i % 4),
    delay: (i * 0.5) % 20,
    duration: 12 + (i % 15),
    opacity: 0.2 + (i % 5) * 0.08,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: p.left,
            bottom: "-4px",
            width: `${p.size}px`,
            height: `${p.size}px`,
            opacity: 0,
            background: `linear-gradient(135deg, #DCA0FA, #E6BEFA)`,
            animation: `particleDrift ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
      {/* Subtle radial gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 80%, rgba(220, 160, 250, 0.05) 0%, transparent 60%)",
        }}
      />
      {/* Additional accent glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(230, 190, 250, 0.03) 0%, transparent 50%)",
        }}
      />
    </div>
  );
}

// =============================================================================
// Animated Gradient Border Card
// =============================================================================

function GradientBorderCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      {/* Animated gradient border */}
      <div
        className="absolute -inset-[1px] rounded-2xl opacity-60"
        style={{
          background: "conic-gradient(from var(--angle, 0deg), #DCA0FA, #E6BEFA, #DCA0FA)",
          animation: "gradient-rotate 3s linear infinite",
        }}
      />
      {/* Inner content */}
      <div className="relative rounded-2xl bg-hvme-bg-surface/95 backdrop-blur-xl p-8 md:p-12">
        {children}
      </div>
    </div>
  );
}

// =============================================================================
// Privacy Mode Card
// =============================================================================

function PrivacyCard({
  option,
  selected,
  onStartSession,
}: {
  option: PrivacyOption;
  selected: boolean;
  onStartSession: (mode: PrivacyMode) => void;
}) {
  const policy = PRIVACY_DEFAULTS[option.mode];

  return (
    <motion.button
      onClick={() => onStartSession(option.mode)}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`
        relative w-full text-left p-5 rounded-xl border transition-all duration-300
        ${selected
          ? "border-hvme-primary/50 bg-hvme-bg-mid/80 shadow-primary-glow"
          : "border-hvme-border bg-hvme-bg-deep/50 hover:border-hvme-border-hover hover:bg-hvme-bg-mid/30"
        }
      `}
    >
      {/* Selection indicator */}
      {selected && (
        <motion.div
          layoutId="privacy-selection"
          className="absolute inset-0 rounded-xl border border-hvme-primary/40"
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{
            boxShadow:
              "0 0 20px rgba(220, 160, 250, 0.15), inset 0 0 20px rgba(220, 160, 250, 0.05)",
          }}
        />
      )}

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <span
            className={`text-lg ${selected ? "text-hvme-primary" : "text-hvme-text-muted"}`}
          >
            {option.icon}
          </span>
          <h3
            className={`text-sm font-semibold font-body ${selected ? "text-hvme-primary" : "text-hvme-text-primary"}`}
          >
            {option.title}
          </h3>
        </div>

        {/* Description */}
        <p className="text-xs text-hvme-text-secondary mb-3 font-body">
          {option.description}
        </p>

        {/* Details list */}
        <ul className="space-y-1">
          {option.details.map((detail) => (
            <li
              key={detail}
              className="flex items-center gap-2 text-[10px] text-hvme-text-muted font-body"
            >
              <span
                className={`w-1 h-1 rounded-full ${selected ? "bg-hvme-primary" : "bg-hvme-text-muted"}`}
              />
              {detail}
            </li>
          ))}
        </ul>

        {/* TTL Badge */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded text-[9px] font-mono bg-hvme-bg-deep border border-hvme-border text-hvme-text-muted">
            TTL {policy.defaultTtlSeconds}s
          </span>
          {!policy.allowPinning && (
            <span className="px-2 py-0.5 rounded text-[9px] font-mono bg-hvme-accent/10 border border-hvme-accent/20 text-hvme-accent">
              NO PIN
            </span>
          )}
          {!policy.allowRemoteStorage && (
            <span className="px-2 py-0.5 rounded text-[9px] font-mono bg-hvme-primary/10 border border-hvme-primary/20 text-hvme-primary">
              LOCAL ONLY
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
}

// =============================================================================
// Scroll Indicator
// =============================================================================

function ScrollIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.2, duration: 0.5 }}
      className="fixed bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-40"
    >
      <span className="text-xs text-hvme-text-muted font-body tracking-wide">Scroll to explore</span>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="text-hvme-text-muted"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
// Session Lobby
// =============================================================================

export function SessionLobby({ onStartSession, hasActiveSession, onLearnMore }: SessionLobbyProps) {
  const [selectedMode, setSelectedMode] = useState<PrivacyMode>("standard");
  const [showPrivacyOptions, setShowPrivacyOptions] = useState(false);

  const handleStart = () => {
    onStartSession(selectedMode);
  };

  const handleGetStarted = () => {
    setShowPrivacyOptions(true);
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center overflow-x-hidden">
      {/* Particle background - KEPT as required */}
      <ParticleBackground />

      {/* Hero Section - centered in viewport */}
      <div className="min-h-screen flex flex-col items-center justify-center w-full">
        {/* Main Content */}
        <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-4xl px-6"
      >
        <GradientBorderCard>
          {/* Hero Content */}
          {!showPrivacyOptions ? (
            <div className="text-center">
              {/* Gradient Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="font-heading font-black text-5xl md:text-7xl tracking-[-0.02em] mb-6"
                style={{
                  background: "linear-gradient(135deg, #DCA0FA, #E6BEFA)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Sovereign AI Infrastructure
              </motion.h1>

              {/* Subheadline with rotating words */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="font-body font-normal text-lg md:text-xl text-hvme-text-secondary mb-6 max-w-xl mx-auto"
              >
                <RotatingWords
                  words={["FHE-native", "AI-aware", "Privacy by design", "Enterprise-ready"]}
                  className="text-hvme-primary font-semibold"
                  interval={2500}
                />{" "}
                infrastructure for the sovereign enterprise.
              </motion.p>

              {/* FOMO: Scarcity Badge */}
              {TOTAL_SPOTS - CURRENT_SIGNUPS <= 200 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                  className="mb-6"
                >
                  <ScarcityBadge
                    count={TOTAL_SPOTS - CURRENT_SIGNUPS}
                    label="Genesis spots left"
                    size="md"
                  />
                </motion.div>
              )}

              {/* FOMO: Countdown Timer */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="mb-8"
              >
                <CountdownTimer
                  targetDate={GENESIS_LAUNCH_DATE}
                  title="GENESIS NODE ALLOCATION CLOSES IN"
                  variant="hero"
                />
              </motion.div>

              {/* FOMO: Signup Progress Bar */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                className="mb-10 max-w-md mx-auto"
              >
                <SignupProgressBar
                  current={CURRENT_SIGNUPS}
                  total={TOTAL_SPOTS}
                  label="Genesis Node Allocation"
                  size="md"
                  urgencyThreshold={80}
                />
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                {/* Primary CTA - Get Started */}
                <motion.button
                  onClick={handleGetStarted}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="
                    relative px-8 py-3.5 rounded-full text-base font-semibold font-body
                    bg-hvme-primary text-white
                    transition-all duration-300
                    hover:shadow-primary-glow-lg
                  "
                  style={{
                    boxShadow: "0 0 20px rgba(220, 160, 250, 0.3)",
                  }}
                >
                  Get Started
                </motion.button>

                {/* Secondary CTA - Learn More */}
                <motion.button
                  onClick={onLearnMore}
                  whileHover={{ scale: 1.03, borderColor: "rgba(220, 160, 250, 0.5)" }}
                  whileTap={{ scale: 0.97 }}
                  className="
                    px-8 py-3.5 rounded-full text-base font-semibold font-body
                    bg-transparent text-hvme-text-primary
                    border border-hvme-border-hover
                    transition-all duration-300
                    hover:border-hvme-primary/50 cursor-pointer
                  "
                >
                  Learn More
                </motion.button>
              </motion.div>
            </div>
          ) : (
            /* Privacy Mode Selection (Session Lobby Functionality) */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Back Button */}
              <motion.button
                onClick={() => setShowPrivacyOptions(false)}
                className="mb-6 text-sm text-hvme-text-muted hover:text-hvme-text-secondary transition-colors font-body flex items-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </motion.button>

              {/* Title */}
              <div className="text-center mb-8">
                <h2
                  className="font-heading font-bold text-2xl md:text-3xl tracking-[-0.02em] mb-3"
                  style={{
                    background: "linear-gradient(135deg, #DCA0FA, #E6BEFA)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Select Privacy Mode
                </h2>
                <p className="text-sm text-hvme-text-secondary font-body max-w-md mx-auto">
                  Choose your privacy posture and memory policy before entering the
                  orchestration environment.
                </p>
              </div>

              {/* Privacy Mode Selection - Click to start session */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {PRIVACY_OPTIONS.map((option) => (
                  <PrivacyCard
                    key={option.mode}
                    option={option}
                    selected={selectedMode === option.mode}
                    onStartSession={onStartSession}
                  />
                ))}
              </div>

              {/* Start Button */}
              <div className="text-center">
                <motion.button
                  onClick={handleStart}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="
                    relative px-10 py-3.5 rounded-full text-base font-semibold font-body
                    bg-hvme-primary text-white
                    transition-all duration-300
                    hover:shadow-primary-glow-lg
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                  style={{
                    boxShadow: "0 0 20px rgba(220, 160, 250, 0.3)",
                  }}
                >
                  {hasActiveSession ? "Restart Session" : "Start Session"}
                </motion.button>

                <p className="mt-4 text-xs text-hvme-text-muted font-body">
                  Privacy mode:{" "}
                  <span className="text-hvme-text-secondary font-medium">
                    {selectedMode}
                  </span>{" "}
                  &middot; TTL:{" "}
                  <span className="font-mono text-hvme-text-secondary">
                    {PRIVACY_DEFAULTS[selectedMode].defaultTtlSeconds}s
                  </span>
                </p>
              </div>
            </motion.div>
          )}
        </GradientBorderCard>
      </motion.div>
      </div>

      {/* Scroll Indicator */}
      {!showPrivacyOptions && <ScrollIndicator />}

      {/* FOMO: Live Activity Feed */}
      {!showPrivacyOptions && (
        <LiveActivityFeed
          demoMode={true}
          maxItems={3}
          position="bottom-right"
          dismissAfter={5000}
        />
      )}

      {/* Features Preview Section - gives users something to scroll to */}
      {!showPrivacyOptions && (
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative z-10 w-full max-w-6xl mx-auto px-6 py-24"
        >
          <h2 className="text-center font-heading font-bold text-3xl md:text-4xl mb-4"
            style={{
              background: "linear-gradient(135deg, #DCA0FA, #E6BEFA)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Why HVME?
          </h2>
          <p className="text-center text-hvme-text-secondary mb-12 max-w-2xl mx-auto">
            The first privacy infrastructure built for the AI era.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: "🔐",
                title: "FHE-Native Compute",
                description: "Process encrypted data without ever decrypting. True end-to-end privacy for AI workloads."
              },
              {
                icon: "🤖",
                title: "AI-Aware Protocols",
                description: "Purpose-built for LLM inference, vector search, and agent orchestration at scale."
              },
              {
                icon: "🏛️",
                title: "Sovereign by Design",
                description: "Your data never leaves your control. Run on your infrastructure or ours."
              }
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="p-6 rounded-2xl border border-hvme-border bg-hvme-bg-surface/50 backdrop-blur-sm hover:border-hvme-primary/30 transition-colors"
              >
                <span className="text-3xl mb-4 block">{feature.icon}</span>
                <h3 className="font-heading font-semibold text-lg text-hvme-text-primary mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-hvme-text-secondary">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}

      {/* Stats Section */}
      {!showPrivacyOptions && (
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative z-10 w-full max-w-4xl mx-auto px-6 py-16 mb-24"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "100M+", label: "FHE Operations" },
              { value: "50+", label: "Node Operators" },
              { value: "99.9%", label: "Uptime" },
              { value: "<10ms", label: "Latency" }
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
              >
                <div className="font-heading font-black text-3xl md:text-4xl text-hvme-primary mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-hvme-text-muted">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}
    </div>
  );
}
